import { useState } from "react";
import { DateInput, TextInput, Dropdown, MultiInput, PhoneInput, EmailInput, Checkbox, useTranslator, getLocale } from "@sia/style-guide";
import { useCallback, useContext, useEffect } from "react";
import { WizarContext } from "contexts/WizardContext";
import { juniorWorkPlaceInfoActionCreator } from "contexts/WizardContext";
import webPlatformAdapter from "common/Adapters/WebPlatformAdapter/WebPlatformAdapter";
import pgAdapter from "common/Adapters/PGAdapter/PGAdapter";
import GlobalContext from "contexts/GlobalContext";
import { initialWizardFormState } from "contexts/WizardContext";
import { composeAddressLabel } from "pages/Wizard/utils";
const WorkplaceInfo = ({ errors, setErrors }) => {
  const translate = useTranslator();
  const { language } = useContext(GlobalContext);
  const { state, dispatch } = useContext(WizarContext);
  let workplaceInfo = state.junior.workplaceInfo;
  let workplaceInfoErrors = errors.junior.workplaceInfo;
  let changState = useCallback((payload) => dispatch(juniorWorkPlaceInfoActionCreator(payload)), [dispatch]);
  const resetInputFieldError = (property, error) => {
    const exculdedProperties = ["phone", "mobile", "email"];
    if (!exculdedProperties.includes(property)) {
      setErrors((errorsState) => ({
        ...errorsState,
        junior: {
          ...errorsState.junior,
          workplaceInfo: { ...errorsState.junior.workplaceInfo, [property]: error },
        },
      }));
    } else {
      exculdedProperties.forEach((exculdedProperty) => {
        if (property === exculdedProperty) {
          setErrors((errorsState) => ({
            ...errorsState,
            junior: {
              ...errorsState.junior,
              workplaceInfo: { ...errorsState.junior.workplaceInfo, [exculdedProperty]: error },
            },
          }));
        }
      });
    }
  };

  const { user } = useContext(GlobalContext);

  const [addresses, setAddresses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cantons, setCantons] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [jobFunctions, setJobFunctions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      let lang = getLocale(language);

      const data_addresses = await webPlatformAdapter.request("GET", "rpc/get_addresses_single", { lang });
      let addresses = data_addresses.filter((e) => e.label !== null);
      addresses = addresses.map((e) => ({ id: e.id, label: composeAddressLabel(e)}));
      setAddresses(addresses);
      const data_countries = await webPlatformAdapter.request("GET", "rpc/get_countries_list", { lang });
      let countries = data_countries.map((e) => ({ ...e, label: e.name }));
      setCountries(countries);
      const data_states = await webPlatformAdapter.request("GET", "rpc/get_states_list", { lang });
      let cantons = data_states.map((e) => ({ ...e, label: e.name }));
      setCantons(cantons);
      const data_work_types = await webPlatformAdapter.request("GET", "rpc/get_work_types_list", { lang });
      let professions = data_work_types.map((e) => ({ ...e, label: e.name }));
      setProfessions(professions);
      const data_functions = await webPlatformAdapter.request("GET", "rpc/get_work_functions_list", { lang });
      let jobFunctions = data_functions.map((e) => ({ ...e, label: e.name }));
      setJobFunctions(jobFunctions);
      const data_work_branches = await webPlatformAdapter.request("GET", "rpc/get_work_branches_list", { lang });
      let branches = data_work_branches.map((e) => ({ ...e, label: e.name }));
      setBranches(branches);
      const data_work_specialisation = await webPlatformAdapter.request("GET", "rpc/get_work_specialisation_list", { lang });
      let activities = data_work_specialisation.map((e) => ({ ...e, label: e.name }));
      setActivities(activities);
    };
    if (user) {
      fetchData();
    }
  }, [user, language]);

  useEffect(() => {
    if (user) {
      pgAdapter.getWizardSession(({ data }) => {
        const defaultWorkplaceInfo = { ...initialWizardFormState.junior.workplaceInfo };
        const { workplaceInfo } = data?.junior || {};
        const {
          hasWorkplace,
          currentAddress,
          companyName,
          department,
          street,
          zip,
          city,
          country,
          canton,
          phone,
          mobile,
          email,
          since,
          profession,
          jobFunction,
          branch,
          activities,
        } = workplaceInfo || defaultWorkplaceInfo;
        changState({
          hasWorkplace,
          currentAddress,
          companyName,
          department,
          street,
          zip,
          city,
          country,
          canton,
          phone: phone || "",
          mobile: mobile || "",
          email: email || "",
          since,
          profession,
          jobFunction,
          branch,
          activities,
        });
      });
    }
  }, [user, changState]);

  const setUserSession = (obj) => {
    pgAdapter.getWizardSession(({ data }) => {
      const updatedSession = { ...data, junior: { ...(data.junior || {}), workplaceInfo: { ...(data?.junior?.workplaceInfo || {}), ...obj } } };
      pgAdapter.setWizardSession({ ...updatedSession });
    });
  };

  return (
    <div className="worplace-info-page">
      <Checkbox
        id={"junior-workplace-page-hasworkplace"}
        // TODO: add the following string to shared
        label={translate("Ich habe im Moment keinen Arbeitsplatz.")}
        value={workplaceInfo.hasWorkplace}
        onChange={(event, value) => {
          resetInputFieldError("language");
          changState({ hasWorkplace: value });
          setUserSession({ hasWorkplace: value });
        }}
      />

      <p className="productive-heading-03 sia-text-02 sia-margin-03-top sia-margin-03-bottom">
        {translate("wizard.form.single.work-place.current.title")}
      </p>
      <p className="sia-text-04 sia-margin-02-bottom">{translate("wizard.form.single.work-place.current.select")}</p>

      <Dropdown
        id={"junior-workplace-page-company"}
        label={translate("wizard.form.single.work-place.current.label")}
        items={addresses}
        value={workplaceInfo.currentAddress}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("currentAddress");
          changState({ currentAddress: value });
          setUserSession({ currentAddress: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalid={workplaceInfoErrors.currentAddress}
        invalidText={workplaceInfoErrors.currentAddress}
      />
      <p className="sia-text-04 sia-margin-02-bottom">{translate("wizard.form.single.work-place.new.title")}</p>
      <TextInput
        onBlur={() => {
          if (workplaceInfo.companyName) {
            setUserSession({ companyName: workplaceInfo.companyName });
          }
        }}
        id={"junior-personal-page-company-name"}
        value={workplaceInfo.companyName}
        placeholder={translate("wizard.form.single.work-place.new.company.name.placeholder")}
        onChange={(event, value) => {
          resetInputFieldError("companyName");
          changState({ companyName: value });
        }}
        label={translate("wizard.form.single.work-place.new.company.name.label")}
        required={true}
        invalidPatternText={workplaceInfoErrors.companyName}
        invalidText={workplaceInfoErrors.companyName}
      />
      <TextInput
        onBlur={() => {
          if (workplaceInfo.department) {
            setUserSession({ department: workplaceInfo.department });
          }
        }}
        id={"junior-personal-page-department"}
        value={workplaceInfo.department}
        placeholder={translate("wizard.form.single.work-place.new.company.department.placeholder")}
        onChange={(event, value) => {
          resetInputFieldError("department");
          changState({ department: value });
        }}
        label={translate("wizard.form.single.work-place.new.company.department.label")}
        invalidPatternText={workplaceInfoErrors.department}
        invalidText={workplaceInfoErrors.department}
      />
      <TextInput
        onBlur={() => {
          if (workplaceInfo.street) {
            setUserSession({ street: workplaceInfo.street });
          }
        }}
        id={"junior-personal-page-street"}
        value={workplaceInfo.street}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("street");
          changState({ street: value });
        }}
        label={translate("wizard.form.single.work-place.new.company.street.label")}
        invalidPatternText={workplaceInfoErrors.street}
        invalidText={workplaceInfoErrors.street}
      />
      <TextInput
        onBlur={() => {
          if (workplaceInfo.zip) {
            setUserSession({ zip: workplaceInfo.zip });
          }
        }}
        id={"junior-personal-page-zip"}
        value={workplaceInfo.zip}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("zip");
          changState({ zip: value });
        }}
        label={translate("wizard.form.single.work-place.new.company.zip.label")}
        invalidPatternText={workplaceInfoErrors.zip}
        invalidText={workplaceInfoErrors.zip}
      />
      <TextInput
        onBlur={() => {
          if (workplaceInfo.city) {
            setUserSession({ city: workplaceInfo.city });
          }
        }}
        id={"junior-personal-page-city"}
        value={workplaceInfo.city}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("city");
          changState({ city: value });
        }}
        label={translate("wizard.form.single.work-place.new.company.city.label")}
        invalidPatternText={workplaceInfoErrors.city}
        invalidText={workplaceInfoErrors.city}
      />
      <Dropdown
        id={"junior-workplace-page-country"}
        label={translate("wizard.form.single.work-place.new.company.country.label")}
        items={countries}
        value={workplaceInfo.country}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("country");
          changState({ country: value });
          setUserSession({ country: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalid={workplaceInfoErrors.country}
        invalidText={workplaceInfoErrors.country}
      />
      <Dropdown
        id={"junior-workplace-page-canton"}
        label={translate("wizard.form.single.work-place.new.company.canton.label")}
        items={cantons}
        value={workplaceInfo.canton}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("canton");
          changState({ canton: value });
          setUserSession({ canton: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalid={workplaceInfoErrors.canton}
        invalidText={workplaceInfoErrors.canton}
      />
      <MultiInput
        id={"junior-workplace-page-contact"}
        invalidText={null}
        translate={translate}
        onBlur={() => {
          if (workplaceInfo.phone) {
            setUserSession({ phone: workplaceInfo.phone });
          }
          if (workplaceInfo.mobile) {
            setUserSession({ mobile: workplaceInfo.mobile });
          }
          if (workplaceInfo.email) {
            setUserSession({ email: workplaceInfo.email });
          }
        }}
      >
        <PhoneInput
          id={"junior-workplace-page-phone"}
          label={translate("wizard.form.single.work-place.new.company.telephone.label")}
          value={workplaceInfo.phone}
          onChange={(event, value, error) => {
            resetInputFieldError("phone", error);
            changState({ phone: value });
          }}
          invalidPatternText={translate("form.input.error.invalid.with-example", { example: translate("form.input.error.invalid.example.phone") })}
          invalidText={workplaceInfoErrors.phone}
        />

        <PhoneInput
          id={"junior-workplace-page-mobile"}
          label={translate("wizard.form.single.work-place.new.company.mobile.label")}
          value={workplaceInfo.mobile}
          onChange={(event, value, error) => {
            resetInputFieldError("mobile", error);
            changState({ mobile: value });
          }}
          invalidPatternText={translate("form.input.error.invalid.with-example", { example: translate("form.input.error.invalid.example.phone") })}
          invalidText={workplaceInfoErrors.mobile}
        />

        <EmailInput
          id={"junior-workplace-page-email"}
          label={translate("wizard.form.single.work-place.new.company.email.label")}
          value={workplaceInfo.email}
          onChange={(event, value, error) => {
            resetInputFieldError("email", error);
            changState({ email: value });
          }}
          invalidPatternText={translate("form.input.error.invalid.with-example", { example: translate("form.input.error.invalid.example.email") })}
          invalidText={workplaceInfoErrors.email}
        />
      </MultiInput>
      <p className="productive-heading-03 sia-text-02 sia-margin-03-top sia-margin-03-bottom">
        {translate("wizard.form.single.work-place.activity.label")}
      </p>
      <DateInput
        onBlur={() => {
          if (workplaceInfo.since) {
            setUserSession({ since: workplaceInfo.since });
          }
        }}
        id={"junior-personal-page-since"}
        required={true}
        placeholder={"01.01.2000"}
        label={translate("wizard.form.single.work-place.activity.since.label")}
        value={workplaceInfo.since}
        onChange={(event, value, error) => {
          resetInputFieldError("since", error);
          changState({ since: value });
        }}
        invalidPatternText={translate("form.input.error.invalid.with-example", { example: "d.m.yyyy" })}
        invalidText={workplaceInfoErrors.since}
      />
      <Dropdown
        id={"junior-workplace-page-prfoession"}
        label={translate("wizard.form.single.work-place.activity.profession.label")}
        items={professions}
        value={workplaceInfo.profession}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("prfoession");
          changState({ profession: value });
          setUserSession({ profession: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalid={workplaceInfoErrors.profession}
        invalidText={workplaceInfoErrors.profession}
      />
      <Dropdown
        id={"junior-workplace-page-function"}
        label={translate("wizard.form.single.work-place.activity.function.label")}
        items={jobFunctions}
        value={workplaceInfo.jobFunction}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("function");
          changState({ jobFunction: value });
          setUserSession({ jobFunction: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalid={workplaceInfoErrors.function}
        invalidText={workplaceInfoErrors.function}
      />
      <Dropdown
        id={"junior-workplace-page-branch"}
        label={translate("wizard.form.single.work-place.activity.branch.label")}
        items={branches}
        value={workplaceInfo.branch}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("branch");
          changState({ branch: value });
          setUserSession({ branch: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalid={workplaceInfoErrors.branch}
        invalidText={workplaceInfoErrors.branch}
      />
      <Dropdown
        id={"junior-workplace-page-activities"}
        label={translate("wizard.form.single.work-place.activity.activity-area.label")}
        items={activities}
        value={workplaceInfo.activities}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("activities");
          changState({ activities: value });
          setUserSession({ activities: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalid={workplaceInfoErrors.activities}
        invalidText={workplaceInfoErrors.activities}
      />
    </div>
  );
};

export default WorkplaceInfo;
